'use client';

import { ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { returnRefAndGenerateDataTracker } from '@/components/atoms/button/utils';
import { InputLabel } from '@/components/atoms/input/InputLabel';
import { WithRef } from '@/components/types';

import Button from '../button/index';

import { safariSvgFix } from '@/utils/browsers';
import IconInfo from '~/svg/IconInfo.svg';
import IconSearch from '~/svg/IconSearch.svg';

// NOTE: sharedStyles is used in both input and button components, when moving to Decodo check file stylesDC.ts
export const sharedStyles =
  'bg-neutral-N00 hover:border-neutral-N99 focus:!border-primary-P99 disabled:bg-neutral-N04 hover:disabled:border-neutral-N20 placeholder-neutral-N50 text-neutral-N99 border-neutral-N20 dark:placeholder-neutral-N50 dark:border-neutral-dark-N50 rounded border font-serif outline-none';

const commonStyles = cn('w-full h-full inline-flex gap-2', sharedStyles);

const heights = {
  m: 'h-10',
  s: 'h-8',
} as const;

const sizes = {
  m: 'text-[1rem] lineHeight-[2.5rem]',
  s: 'text-[0.875rem] lineHeight-[2.25rem]',
} as const;

const iconLeftPaddings = {
  m: 'px-4',
  s: 'pl-4 pr-2 py-1',
} as const;

type ClassNameConfig = {
  root?: string;
  disabled?: string;
  error?: string;
};
type ConfigurableClassNameElement = 'input' | 'label' | 'bottomText';
type StyleCollection = { [K in ConfigurableClassNameElement]: string };

export interface InputFieldProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'size' | 'onChange' | 'className' | 'disabled'
  > {
  classNames?: {
    root?: string;
    leftIcon?: string;
    rightIcon?: string;
    search?: string;
    input?: ClassNameConfig;
    label?: ClassNameConfig;
    bottomText?: ClassNameConfig;
  };
  // TODO move out isSearch to a separate component and probably juse use it with rightIcon
  isSearch?: boolean;
  size?: keyof typeof sizes;
  bottomText?: string;
  name: string;
  label?: ReactNode;
  error?: string;
  hasBottomHelpIcon?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const Input = ({
  classNames,
  name,
  label,
  isSearch,
  size = 'm',
  bottomText,
  error,
  hasBottomHelpIcon,
  isDisabled,
  isRequired,
  leftIcon,
  rightIcon,
  onChange,
  ref,
  ...props
}: WithRef<HTMLInputElement, InputFieldProps>) => {
  const combinedRootClasses = cn(commonStyles, heights[size], sizes[size]);

  const stdStyles: StyleCollection = {
    input: cn(
      combinedRootClasses,
      isSearch ? 'rounded-e-md' : 'rounded border',
      isSearch || rightIcon ? 'pl-4 pr-12' : 'px-4',
      !!leftIcon && 'pl-10 pr-4',
      classNames?.input?.root
    ),
    label: cn('text-neutral-N94', classNames?.label?.root),
    bottomText: cn(
      'text-body-s pointer-events-none flex h-4 gap-1 text-neutral-N94 dark:text-neutral-dark-N00',
      classNames?.bottomText?.root
    ),
  };

  const errorStyles: StyleCollection = {
    input: cn(
      'border-error-E99 dark:border-error-E99',
      classNames?.input?.error
    ),
    label: cn('text-error-E99 dark:text-error-E99', classNames?.label?.error),
    bottomText: cn(
      'text-error-E99 dark:text-error-E99',
      classNames?.bottomText?.error
    ),
  };

  // NOTE: if some disabled styles fail to get picked up, apply them via the `disalbed:` pseudoclass
  const disabledStyles: StyleCollection = {
    input: cn(
      'hover:disabled:border-neutral-N20 disabled:border-neutral-N20',
      classNames?.input?.disabled
    ),
    label: classNames?.label?.disabled || '',
    bottomText: cn('text-neutral-N50', classNames?.bottomText?.disabled),
  };

  const getPrioritisedStyles = (
    element: ConfigurableClassNameElement
  ): string =>
    cn(
      stdStyles[element],
      isDisabled && disabledStyles[element],
      error && errorStyles[element]
    );

  return (
    <div className={cn('relative', classNames?.root)}>
      <div className='relative flex flex-col items-start justify-end'>
        {leftIcon && (
          <div
            className={cn(
              'pointer-events-none absolute grid h-10 w-10 items-center',
              heights[size],
              iconLeftPaddings[size],
              classNames?.leftIcon
            )}
          >
            {leftIcon}
          </div>
        )}
        {label && (
          <InputLabel
            fieldName={name}
            label={label}
            required={isRequired}
            classNames={{
              label: getPrioritisedStyles('label'),
            }}
          />
        )}
        <input
          disabled={isDisabled}
          type='text'
          onChange={onChange}
          name={name}
          className={getPrioritisedStyles('input')}
          data-tracker-type='input'
          data-tracker-context={name}
          ref={(_ref) => {
            returnRefAndGenerateDataTracker(_ref, ref);
          }}
          {...props}
        />
        {isSearch && (
          <Button
            variant='text'
            data-tracker-component='search-button'
            className={cn(
              'text-neutral-N00 absolute end-0 grid h-10 w-10 rounded-e px-3',
              size === 's' ? 'py-2' : 'py-2.5',
              heights[size],
              isDisabled ? 'bg-neutral-N50' : 'bg-neutral-N99',
              classNames?.search
            )}
          >
            <IconSearch {...safariSvgFix({ size: 16 })} />
          </Button>
        )}
        {rightIcon && (
          <div
            className={cn(
              'pointer-events-none absolute end-0 grid h-4 w-4 pl-2 pr-8',
              heights[size],
              classNames?.rightIcon
            )}
          >
            {rightIcon}
          </div>
        )}
      </div>
      <div
        data-tracker-input-bottom={name}
        {...(error && { 'data-tracker-error': error })}
        className={getPrioritisedStyles('bottomText')}
      >
        {hasBottomHelpIcon && <IconInfo />}
        {error || bottomText}
      </div>
    </div>
  );
};
